import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import SettingsIcon from "@mui/icons-material/Settings";
import HistoryIcon from "@mui/icons-material/History";
import {
  EtaxAutoSendAlerts,
  EtaxAutoSendSettings,
} from "../../../types/etax-auto-send.type";
import { numberWithCommas } from "../../../utils";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import {
  AlertCardMeta,
  AlertCardTone,
  DATE_RANGED_CAPTION,
  getAlertCardCount,
  getVisibleAlertCards,
} from "./autoSendMeta";
import { RoundStatusChip } from "./autoSendCells";
import { LoadErrorState } from "../documentCells";

const compact = "@media " + COMPACT_QUERY;

interface AlertsStripProps {
  alerts?: EtaxAutoSendAlerts;
  settings?: EtaxAutoSendSettings;
  loading: boolean;
  /** Opens the documents list on the filter (and range, if any) that lists what a card counts. */
  onSelectCard: (card: AlertCardMeta) => void;
  onOpenSettings: () => void;
  onOpenRounds: () => void;
  /** The alerts request failed: say so with a retry instead of a row of "-". */
  error?: boolean;
  onRetry?: () => void;
}

const AlertCard = ({
  label,
  count,
  tone,
  caption,
  loading,
  onClick,
}: {
  label: string;
  count?: number;
  tone: AlertCardTone;
  caption?: string;
  loading: boolean;
  onClick: () => void;
}) => {
  // An info card reports what the system is already doing about it, and a neutral card what
  // eTax can no longer change, so neither ever turns red.
  const alarming = tone === "alert" && (count ?? 0) > 0;
  return (
    <ButtonBase
      onClick={onClick}
      disabled={loading}
      sx={{
        flex: "1 1 150px",
        minWidth: 140,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        gap: 0.25,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: (theme) =>
          `1px solid ${alarming ? theme.palette.error.main : theme.palette.draft.main}`,
        bgcolor: (theme) => (alarming ? `${theme.palette.error.main}14` : "transparent"),
        [compact]: { px: 1.25, py: 0.5 },
      }}
    >
      <Typography
        variant="caption"
        color={alarming ? "error" : "text.secondary"}
        fontWeight={500}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={32} height={32} />
      ) : (
        <Typography
          variant="h6"
          fontWeight={700}
          color={alarming ? "error" : "text.primary"}
          lineHeight={1.3}
        >
          {count === undefined ? "-" : numberWithCommas(count)}
        </Typography>
      )}
      {caption && (
        <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
          {caption}
        </Typography>
      )}
    </ButtonBase>
  );
};

/**
 * What needs attention in the eTax pipeline right now, and where the weekly round stands.
 *
 * Most counts are company-wide rather than scoped to the dashboard's date range — a refused
 * bill or a rejection from last month still needs fixing or acknowledging this month — and open
 * the documents list with no date range. The held / expired cards count only bills in the
 * range, say so in a caption, and open the list on that same range, so every card's figure
 * matches the list it opens.
 */
export default function AlertsStrip({
  alerts,
  settings,
  loading,
  onSelectCard,
  onOpenSettings,
  onOpenRounds,
  error,
  onRetry,
}: AlertsStripProps) {
  const lastRound = alerts?.last_round ?? null;
  const lastRoundTime =
    lastRound &&
    formatThaiShortDateTimeBkk(
      lastRound.finished_at ?? lastRound.last_attempt_at ?? lastRound.started_at,
    );
  // Settings decide whether the round is on; before they load, a missing next round is the
  // only sign available.
  const enabled = settings ? settings.enabled : Boolean(alerts?.next_round_at);
  const nextRoundAt = settings?.next_round_at ?? alerts?.next_round_at ?? null;

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        display: "flex",
        gap: 2,
        alignItems: "stretch",
        flexWrap: "wrap",
        [compact]: { p: 1.5, gap: 1.5 },
      }}
    >
      <Box display="flex" gap={1} flexWrap="wrap" sx={{ flex: "3 1 640px" }}>
        {error && !loading && onRetry && (
          <Box sx={{ flexBasis: "100%", display: "flex", justifyContent: "flex-start" }}>
            <LoadErrorState compact onRetry={onRetry} />
          </Box>
        )}
        {getVisibleAlertCards(alerts).map((card) => (
          <AlertCard
            key={card.key}
            label={card.label}
            count={getAlertCardCount(alerts, card.key)}
            tone={card.tone}
            caption={card.dateRanged ? DATE_RANGED_CAPTION : undefined}
            loading={loading}
            onClick={() => onSelectCard(card)}
          />
        ))}
      </Box>

      <Box
        sx={{
          flex: "1 1 280px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.75,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            รอบล่าสุด:
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={160} />
          ) : lastRound ? (
            <>
              <RoundStatusChip status={lastRound.status} />
              <Typography variant="body2">
                {lastRoundTime || "-"} · ส่ง {numberWithCommas(lastRound.sent_count)} ใบ
              </Typography>
            </>
          ) : (
            <Typography variant="body2">ยังไม่มีรอบส่ง</Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {loading ? (
            <Skeleton variant="text" width={200} />
          ) : enabled ? (
            <Typography variant="body2">
              <Box component="span" color="text.secondary">
                รอบถัดไป:
              </Box>{" "}
              <b>{formatThaiShortDateTimeBkk(nextRoundAt) || "-"}</b>
            </Typography>
          ) : (
            <Chip
              label="ปิดการส่งอัตโนมัติ"
              size="small"
              sx={{ color: "#6a6a6a", bgcolor: "#6a6a6a1A", fontWeight: 500 }}
            />
          )}
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<SettingsIcon fontSize="small" />}
            onClick={onOpenSettings}
          >
            ตั้งค่าส่งอัตโนมัติ
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<HistoryIcon fontSize="small" />}
            onClick={onOpenRounds}
          >
            ประวัติรอบส่งอัตโนมัติ
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
